import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, Database, Tag, AlertCircle, ArrowRight, RefreshCw, ChevronRight,
} from "lucide-react";

const CATEGORIES = [
  { key: "bank", label: "Banks", desc: "Issuing banks (HDFC, ICICI, IDFC First Bank...)" },
  { key: "card_network", label: "Card Networks", desc: "Visa, Mastercard, RuPay, Amex..." },
  { key: "card_type", label: "Card Types", desc: "Credit Card, Debit Card, Corporate CC, Prepaid..." },
  { key: "pay_mode", label: "Pay Modes", desc: "UPI, Net Banking, EMI, Pay Later, UPI on Credit Line..." },
];

export default function AdminMasterData() {
  const [tab, setTab] = useState("bank");
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessResult, setReprocessResult] = useState(null);

  const bulkReprocess = async () => {
    if (!window.confirm("Re-process ALL past transactions through master data aliases?\nThis may take a moment for large datasets.")) return;
    setReprocessing(true);
    setReprocessResult(null);
    try {
      const { data } = await api.post("/admin/master-data/bulk-reprocess");
      setReprocessResult(data.stats);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Re-process failed");
    } finally { setReprocessing(false); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="admin-master-data-page">
        <span className="overline">Admin · System</span>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-2 flex items-center gap-2">
              <Database className="h-7 w-7 text-primary" strokeWidth={1.5} />
              Master Data & Alias Mapping
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
              Manage canonical banks, card networks, card types, and pay modes.
              Add aliases so messy gateway responses (e.g., "idfc first", "idfc bank", "IDFC") all
              resolve to a single master record. Unmapped values are auto-queued for review.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={bulkReprocess}
            disabled={reprocessing}
            data-testid="bulk-reprocess-btn"
          >
            {reprocessing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" strokeWidth={1.5} />}
            {reprocessing ? "Re-processing..." : "Re-process past txns"}
          </Button>
        </div>

        {/* Re-process results */}
        {reprocessResult && (
          <div className="mt-4 surface-card p-4 border-l-4 border-primary" data-testid="reprocess-results">
            <div className="text-sm font-semibold mb-2">Bulk re-process results</div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 text-center">
              {[
                { label: "Scanned", value: reprocessResult.scanned },
                { label: "Banks updated", value: reprocessResult.bank_updated },
                { label: "Networks updated", value: reprocessResult.network_updated },
                { label: "Modes updated", value: reprocessResult.mode_updated },
                { label: "Newly enriched", value: reprocessResult.newly_enriched },
                { label: "Unmapped queued", value: reprocessResult.unmapped_queued },
              ].map((s) => (
                <div key={s.label}>
                  <div className="text-2xl font-bold font-mono">{s.value}</div>
                  <div className="text-[10px] text-muted-foreground">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="mt-6" data-testid="master-data-tabs">
          <TabsList className="flex-wrap h-auto">
            {CATEGORIES.map((c) => (
              <TabsTrigger key={c.key} value={c.key} data-testid={`tab-${c.key}`}>{c.label}</TabsTrigger>
            ))}
            <TabsTrigger value="unmapped" data-testid="tab-unmapped">
              Unmapped Queue
            </TabsTrigger>
          </TabsList>

          {CATEGORIES.map((c) => (
            <TabsContent key={c.key} value={c.key} className="mt-4">
              <MasterCategoryPanel category={c.key} desc={c.desc} />
            </TabsContent>
          ))}

          <TabsContent value="unmapped" className="mt-4">
            <UnmappedPanel />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

/* ── Master category panel ─────────────────────────────────────── */

function MasterCategoryPanel({ category, desc }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [aliasOpen, setAliasOpen] = useState(null); // record id
  const [form, setForm] = useState({ name: "", code: "", aliases: "" });
  const [busy, setBusy] = useState(false);
  // BBPS import (banks only)
  const [bbpsOpen, setBbpsOpen] = useState(false);
  const [bbpsData, setBbpsData] = useState(null);
  const [bbpsLoading, setBbpsLoading] = useState(false);
  const [bbpsSelected, setBbpsSelected] = useState(new Set());
  const [bbpsImporting, setBbpsImporting] = useState(false);
  // Edit/delete with password
  const [editRecord, setEditRecord] = useState(null);
  const [editName, setEditName] = useState("");
  const [editStatus, setEditStatus] = useState("active");
  const [pwDialog, setPwDialog] = useState(null); // {action, recordId, ...}
  const [pw, setPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/master-data/categories/${category}`);
      setRecords(data.records || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const onCreate = async () => {
    if (!form.name || !form.code) { toast.error("Name and code required"); return; }
    setBusy(true);
    try {
      const aliases = form.aliases.split(",").map((a) => a.trim()).filter(Boolean);
      await api.post(`/admin/master-data/categories/${category}`, { name: form.name, code: form.code, aliases });
      toast.success(`${form.name} created`);
      setCreateOpen(false);
      setForm({ name: "", code: "", aliases: "" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };

  // BBPS biller fetch + import (banks only)
  const fetchBbpsBillers = async () => {
    setBbpsLoading(true);
    setBbpsData(null);
    setBbpsSelected(new Set());
    try {
      const { data } = await api.get("/admin/master-data/bank/fetch-bbps-billers");
      setBbpsData(data);
      // Auto-select all not-yet-mapped
      const sel = new Set();
      (data.suggestions || []).forEach((s, i) => { if (!s.already_mapped) sel.add(i); });
      setBbpsSelected(sel);
      setBbpsOpen(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to fetch billers");
    } finally { setBbpsLoading(false); }
  };

  const [bbpsEditedNames, setBbpsEditedNames] = useState({});
  const editBbpsName = (idx, val) => setBbpsEditedNames((prev) => ({ ...prev, [idx]: val }));

  const importBbpsBanks = async () => {
    if (!bbpsData) return;
    const banks = bbpsData.suggestions
      .filter((_, i) => bbpsSelected.has(i))
      .map((s, i) => {
        const name = bbpsEditedNames[i] || s.suggested_name;
        return {
          name,
          code: name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""),
          aliases: s.biller_names,
        };
      });
    if (banks.length === 0) { toast.error("Select at least one bank"); return; }
    setBbpsImporting(true);
    try {
      const { data } = await api.post("/admin/master-data/bank/import-bbps", { banks });
      toast.success(data.message);
      setBbpsOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally { setBbpsImporting(false); }
  };

  const toggleBbpsSelect = (idx) => {
    setBbpsSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  // Edit record
  const openEdit = (r) => {
    setEditRecord(r);
    setEditName(r.name);
    setEditStatus(r.status);
  };

  const submitEdit = () => {
    if (!editRecord) return;
    setPwDialog({ action: "update", recordId: editRecord.id, name: editName, status: editStatus });
    setPw("");
  };

  const confirmDelete = (r) => {
    setPwDialog({ action: "delete", recordId: r.id, name: r.name });
    setPw("");
  };

  const executePwAction = async () => {
    if (!pwDialog || !pw) { toast.error("Password required"); return; }
    setPwBusy(true);
    try {
      if (pwDialog.action === "update") {
        await api.patch(`/admin/master-data/categories/${category}/${pwDialog.recordId}`, {
          name: pwDialog.name, status: pwDialog.status, password: pw,
        });
        toast.success("Record updated");
        setEditRecord(null);
      } else if (pwDialog.action === "delete") {
        await api.delete(`/admin/master-data/categories/${category}/${pwDialog.recordId}?password=${encodeURIComponent(pw)}`);
        toast.success(`Deleted '${pwDialog.name}'`);
      }
      setPwDialog(null);
      setPw("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    } finally { setPwBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-muted-foreground">{desc}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{records.length} records</p>
        </div>
        <div className="flex items-center gap-2">
          {category === "bank" && (
            <Button size="sm" variant="outline" onClick={fetchBbpsBillers} disabled={bbpsLoading} data-testid="fetch-bbps-btn">
              {bbpsLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Database className="h-3.5 w-3.5 mr-1" />}
              Fetch from BBPS
            </Button>
          )}
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid={`create-${category}`}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground p-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
      ) : records.length === 0 ? (
        <div className="surface-card p-12 text-center">
          <Database className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
          <p className="mt-3 text-sm text-muted-foreground">No records yet. Add your first {category.replace("_", " ")}.</p>
        </div>
      ) : (
        <div className="surface-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="p-3 pl-5">Name</th>
                <th className="p-3">Code</th>
                <th className="p-3 text-center">Aliases</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right pr-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} className="border-b border-border/50 last:border-0 hover:bg-accent/30" data-testid={`record-${r.code}`}>
                  <td className="p-3 pl-5 font-medium">{r.name}</td>
                  <td className="p-3 font-mono text-xs text-muted-foreground">{r.code}</td>
                  <td className="p-3 text-center">
                    <Badge variant="outline" className="font-mono text-[10px]">{r.alias_count}</Badge>
                  </td>
                  <td className="p-3">
                    <Badge variant={r.status === "active" ? "default" : "outline"} className={r.status === "active" ? "bg-success text-success-foreground" : ""}>{r.status}</Badge>
                  </td>
                  <td className="p-3 text-right pr-5">
                    <div className="flex items-center gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-${r.code}`}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setAliasOpen(r.id)} data-testid={`aliases-${r.code}`}>
                        <Tag className="h-3.5 w-3.5 mr-1" /> Aliases
                      </Button>
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => confirmDelete(r)} data-testid={`delete-${r.code}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add {category.replace("_", " ")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name (canonical)</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., IDFC First Bank" data-testid="master-name" />
            </div>
            <div>
              <Label>Code (unique key)</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} placeholder="e.g., idfc_first_bank" className="font-mono" data-testid="master-code" />
            </div>
            <div>
              <Label>Aliases (comma-separated)</Label>
              <Input value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} placeholder="idfc, idfc first, idfc bank, idfc first bank" data-testid="master-aliases" />
              <p className="text-[10px] text-muted-foreground mt-1">These raw gateway values will resolve to this record. Name and code are auto-added.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={onCreate} disabled={busy} data-testid="create-master-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alias management dialog */}
      {aliasOpen && (
        <AliasDialog category={category} recordId={aliasOpen} onClose={() => { setAliasOpen(null); load(); }} />
      )}

      {/* Edit record dialog */}
      <Dialog open={!!editRecord} onOpenChange={() => setEditRecord(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit {category.replace("_", " ")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="edit-name" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRecord(null)}>Cancel</Button>
            <Button onClick={submitEdit} data-testid="edit-submit">Save (requires password)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password confirmation dialog */}
      <Dialog open={!!pwDialog} onOpenChange={() => { setPwDialog(null); setPw(""); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{pwDialog?.action === "delete" ? "Confirm deletion" : "Confirm update"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {pwDialog?.action === "delete" && (
              <p className="text-sm text-destructive">This will permanently delete <b>{pwDialog?.name}</b> and all its aliases.</p>
            )}
            {pwDialog?.action === "update" && (
              <p className="text-sm">Updating <b>{pwDialog?.name}</b>. Enter your admin password to confirm.</p>
            )}
            <div>
              <Label>Admin password</Label>
              <Input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="Enter your password"
                data-testid="pw-confirm-input"
                onKeyDown={(e) => e.key === "Enter" && executePwAction()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwDialog(null); setPw(""); }}>Cancel</Button>
            <Button
              variant={pwDialog?.action === "delete" ? "destructive" : "default"}
              onClick={executePwAction}
              disabled={pwBusy || !pw}
              data-testid="pw-confirm-btn"
            >
              {pwBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {pwDialog?.action === "delete" ? "Delete" : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BBPS biller import dialog (banks only) */}
      {category === "bank" && (
        <Dialog open={bbpsOpen} onOpenChange={setBbpsOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" /> Import banks from BBPS billers
              </DialogTitle>
            </DialogHeader>
            {bbpsData && (
              <BbpsImportTabs
                bbpsData={bbpsData}
                bbpsSelected={bbpsSelected}
                setBbpsSelected={setBbpsSelected}
                toggleBbpsSelect={toggleBbpsSelect}
                bbpsEditedNames={bbpsEditedNames}
                editBbpsName={editBbpsName}
              />
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setBbpsOpen(false)}>Cancel</Button>
              <Button onClick={importBbpsBanks} disabled={bbpsImporting || bbpsSelected.size === 0} data-testid="import-bbps-submit">
                {bbpsImporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Import {bbpsSelected.size} banks
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


/* ── BBPS Import with Category Tabs ────────────────────────────── */

function BbpsImportTabs({ bbpsData, bbpsSelected, setBbpsSelected, toggleBbpsSelect, bbpsEditedNames, editBbpsName }) {
  const [activeTab, setActiveTab] = useState("all");

  const suggestions = bbpsData.suggestions || [];

  // Separate by category
  const ccItems = [];
  const loanItems = [];
  const bothItems = [];
  suggestions.forEach((s, idx) => {
    const cats = s.categories || [];
    const isCC = cats.includes("credit-card");
    const isLoan = cats.includes("loan-emi");
    if (isCC && isLoan) bothItems.push({ ...s, _idx: idx });
    else if (isCC) ccItems.push({ ...s, _idx: idx });
    else if (isLoan) loanItems.push({ ...s, _idx: idx });
    else bothItems.push({ ...s, _idx: idx });
  });

  const tabItems = activeTab === "credit-card" ? ccItems
    : activeTab === "loan-emi" ? loanItems
    : suggestions.map((s, idx) => ({ ...s, _idx: idx }));

  const selectAllInTab = () => {
    const sel = new Set(bbpsSelected);
    tabItems.forEach((s) => { if (!s.already_mapped) sel.add(s._idx); });
    setBbpsSelected(sel);
  };

  const deselectAllInTab = () => {
    const sel = new Set(bbpsSelected);
    tabItems.forEach((s) => sel.delete(s._idx));
    setBbpsSelected(sel);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>{bbpsData.total_billers} billers</span>
        <span>{bbpsData.unique_banks} unique banks</span>
        <span className="text-success">{bbpsData.already_mapped} already mapped</span>
      </div>

      {/* Category tabs */}
      <div className="flex items-center gap-1 border-b border-border pb-1" data-testid="bbps-category-tabs">
        {[
          { key: "all", label: `All (${suggestions.length})` },
          { key: "credit-card", label: `Credit Card (${ccItems.length})` },
          { key: "loan-emi", label: `Loan EMI (${loanItems.length})` },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveTab(t.key)}
            data-testid={`bbps-tab-${t.key}`}
            className={[
              "px-3 py-1.5 text-xs font-medium rounded-t transition-colors",
              activeTab === t.key
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={selectAllInTab}>Select all new</Button>
        <Button size="sm" variant="outline" onClick={deselectAllInTab}>Deselect all</Button>
        <span className="text-xs text-muted-foreground ml-auto">{bbpsSelected.size} selected total</span>
      </div>

      <div className="space-y-1 max-h-[40vh] overflow-y-auto">
        {tabItems.map((s) => {
          const idx = s._idx;
          return (
            <label
              key={idx}
              className={[
                "flex items-center gap-3 px-3 py-2 rounded border cursor-pointer transition-colors",
                s.already_mapped ? "border-border/30 opacity-50"
                  : bbpsSelected.has(idx) ? "border-primary bg-primary/5"
                  : "border-border hover:bg-accent/30",
              ].join(" ")}
              data-testid={`bbps-bank-${idx}`}
            >
              <input
                type="checkbox"
                checked={bbpsSelected.has(idx)}
                onChange={() => toggleBbpsSelect(idx)}
                disabled={s.already_mapped}
                className="rounded"
              />
              <div className="flex-1 min-w-0">
                <Input
                  value={bbpsEditedNames[idx] ?? s.suggested_name}
                  onChange={(e) => editBbpsName(idx, e.target.value)}
                  className="h-7 text-sm font-medium border-dashed bg-transparent px-1 focus-visible:ring-1"
                  data-testid={`bbps-name-${idx}`}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="text-[10px] text-muted-foreground mt-0.5 px-1">
                  Billers: {s.biller_names.slice(0, 3).join(", ")}{s.biller_names.length > 3 && ` +${s.biller_names.length - 3} more`}
                </div>
              </div>
              <div className="shrink-0 flex items-center gap-1">
                {(s.categories || []).map((c) => (
                  <Badge key={c} variant="outline" className="text-[9px]">{c}</Badge>
                ))}
                {s.already_mapped && <Badge className="bg-success text-success-foreground text-[9px]">mapped</Badge>}
              </div>
            </label>
          );
        })}
        {tabItems.length === 0 && (
          <div className="text-center py-6 text-sm text-muted-foreground">No billers in this category.</div>
        )}
      </div>
    </div>
  );
}
/* ── Alias dialog ──────────────────────────────────────────────── */

function AliasDialog({ category, recordId, onClose }) {
  const [aliases, setAliases] = useState([]);
  const [samplePayloads, setSamplePayloads] = useState([]);
  const [masterName, setMasterName] = useState("");
  const [newAlias, setNewAlias] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deletePw, setDeletePw] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedSample, setExpandedSample] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/master-data/categories/${category}/${recordId}/aliases`);
      setAliases(data.aliases || []);
      setSamplePayloads(data.sample_payloads || []);
      setMasterName(data.master_name || "");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addAlias = async () => {
    if (!newAlias.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/master-data/categories/${category}/${recordId}/aliases`, { alias_value: newAlias });
      setNewAlias("");
      await load();
      toast.success("Alias added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const confirmRemoveAlias = async () => {
    if (!deleteTarget || !deletePw) { toast.error("Password required"); return; }
    try {
      await api.delete(`/admin/master-data/aliases/${deleteTarget}?password=${encodeURIComponent(deletePw)}`);
      setDeleteTarget(null);
      setDeletePw("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-primary" /> Aliases for "{masterName}"
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                placeholder="Add alias (e.g., idfc first)"
                className="flex-1"
                data-testid="new-alias-input"
                onKeyDown={(e) => e.key === "Enter" && addAlias()}
              />
              <Button size="sm" onClick={addAlias} disabled={busy} data-testid="add-alias-btn">
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="space-y-1">
              {aliases.map((a) => (
                <div key={a.id} className="flex items-center justify-between px-3 py-2 rounded border border-border/50 text-sm">
                  <span className="font-mono text-xs">{a.alias_value}</span>
                  <button type="button" onClick={() => { setDeleteTarget(a.id); setDeletePw(""); }} className="p-1 rounded hover:bg-destructive/10 text-destructive">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {aliases.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">No aliases yet.</p>}
            </div>
            {/* Sample raw gateway payloads from unmapped values that resolved
                to this master — gives admins concrete examples of what each
                alias looks like in real PG responses. */}
            {samplePayloads.length > 0 && (
              <div className="border-t border-border pt-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                  Sample gateway payloads ({samplePayloads.length})
                </div>
                <div className="space-y-1.5">
                  {samplePayloads.map((sp, idx) => {
                    const key = `${sp.raw_value}-${idx}`;
                    const open = !!expandedSample[key];
                    return (
                      <div key={key} className="border border-border/50 rounded">
                        <button
                          type="button"
                          onClick={() => setExpandedSample((m) => ({ ...m, [key]: !m[key] }))}
                          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-accent/30 transition"
                          data-testid={`sample-toggle-${idx}`}
                        >
                          <span className="font-mono text-xs">{sp.raw_value}</span>
                          <span className="flex items-center gap-2 text-[10px] text-muted-foreground">
                            <span>seen {sp.occurrence_count}×</span>
                            <ChevronRight className={`h-3 w-3 transition-transform ${open ? "rotate-90" : ""}`} />
                          </span>
                        </button>
                        {open && (
                          <pre className="text-[10px] font-mono whitespace-pre-wrap break-all bg-background border-t border-border p-3 max-h-48 overflow-auto">
{JSON.stringify(sp.sample_payload, null, 2)}
                          </pre>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Inline password confirm for alias deletion */}
            {deleteTarget && (
              <div className="flex items-center gap-2 p-2 rounded bg-destructive/5 border border-destructive/20">
                <Input
                  type="password"
                  value={deletePw}
                  onChange={(e) => setDeletePw(e.target.value)}
                  placeholder="Admin password to delete"
                  className="flex-1 h-8 text-xs"
                  onKeyDown={(e) => e.key === "Enter" && confirmRemoveAlias()}
                />
                <Button size="sm" variant="destructive" onClick={confirmRemoveAlias} disabled={!deletePw}>Delete</Button>
                <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Unmapped Queue ────────────────────────────────────────────── */

function UnmappedPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapDialog, setMapDialog] = useState(null); // item to map
  const [masterRecords, setMasterRecords] = useState([]);
  const [selectedMasterId, setSelectedMasterId] = useState("");
  const [busy, setBusy] = useState(false);
  const [filterCat, setFilterCat] = useState("");
  const [expanded, setExpanded] = useState({});  // {item_id: true}

  const toggleExpand = (id) => setExpanded((m) => ({ ...m, [id]: !m[id] }));

  const load = async () => {
    setLoading(true);
    try {
      const params = filterCat ? `?category=${filterCat}` : "";
      const { data } = await api.get(`/admin/master-data/unmapped${params}`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterCat]);

  const openMap = async (item) => {
    setMapDialog(item);
    setSelectedMasterId("");
    try {
      const { data } = await api.get(`/admin/master-data/categories/${item.category}`);
      setMasterRecords(data.records || []);
    } catch { setMasterRecords([]); }
  };

  const doMap = async () => {
    if (!selectedMasterId || !mapDialog) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/master-data/unmapped/${mapDialog.id}/map`, {
        master_id: selectedMasterId, add_alias: true,
      });
      toast.success(data.message);
      setMapDialog(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Map failed");
    } finally { setBusy(false); }
  };

  const doIgnore = async (id) => {
    try {
      await api.post(`/admin/master-data/unmapped/${id}/ignore`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-muted-foreground">Raw gateway values not yet mapped to any master record. Map them so future transactions auto-resolve.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="text-sm rounded border border-input bg-background px-2 py-1.5">
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground p-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
      ) : items.length === 0 ? (
        <div className="surface-card p-12 text-center">
          <AlertCircle className="h-8 w-8 mx-auto text-success" strokeWidth={1.5} />
          <p className="mt-3 text-sm text-muted-foreground">No unmapped values. All gateway responses are resolving to master records.</p>
        </div>
      ) : (
        <div className="surface-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="p-3 pl-5 w-8"></th>
                <th className="p-3">Raw value</th>
                <th className="p-3">Category</th>
                <th className="p-3 text-center">Seen</th>
                <th className="p-3">First / Last seen</th>
                <th className="p-3 text-right pr-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const hasPayload = item.sample_payload && Object.keys(item.sample_payload).length > 0;
                const isOpen = !!expanded[item.id];
                return (
                  <React.Fragment key={item.id}>
                    <tr className="border-b border-border/50 hover:bg-accent/30" data-testid={`unmapped-${item.id}`}>
                      <td className="p-3 pl-5">
                        {hasPayload ? (
                          <button
                            onClick={() => toggleExpand(item.id)}
                            className="text-muted-foreground hover:text-foreground transition"
                            title={isOpen ? "Hide raw response" : "Show raw response"}
                            data-testid={`toggle-raw-${item.id}`}
                          >
                            <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                          </button>
                        ) : null}
                      </td>
                      <td className="p-3">
                        <span className="font-mono text-sm bg-amber-50 text-amber-800 px-2 py-0.5 rounded">{item.raw_value}</span>
                      </td>
                      <td className="p-3"><Badge variant="outline" className="text-[10px]">{item.category}</Badge></td>
                      <td className="p-3 text-center font-mono">{item.occurrence_count}</td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {item.first_seen && new Date(item.first_seen).toLocaleDateString()}
                        {" — "}
                        {item.last_seen && new Date(item.last_seen).toLocaleDateString()}
                      </td>
                      <td className="p-3 text-right pr-5">
                        <div className="flex items-center gap-1 justify-end">
                          <Button size="sm" variant="default" onClick={() => openMap(item)} data-testid={`map-${item.id}`}>
                            <ArrowRight className="h-3.5 w-3.5 mr-1" /> Map
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => doIgnore(item.id)}>Ignore</Button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && hasPayload && (
                      <tr className="border-b border-border/50 bg-muted/20" data-testid={`raw-payload-${item.id}`}>
                        <td className="p-3 pl-5"></td>
                        <td colSpan={5} className="p-3 pr-5">
                          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                            Raw gateway response (first occurrence)
                          </div>
                          <pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-background border border-border rounded p-3 max-h-64 overflow-auto">
{JSON.stringify(item.sample_payload, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Map dialog */}
      <Dialog open={!!mapDialog} onOpenChange={() => setMapDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Map "{mapDialog?.raw_value}" to a master record</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category: <Badge variant="outline">{mapDialog?.category}</Badge></Label>
            </div>
            <div>
              <Label>Select master record</Label>
              <Select value={selectedMasterId} onValueChange={setSelectedMasterId}>
                <SelectTrigger data-testid="map-master-select"><SelectValue placeholder="Choose..." /></SelectTrigger>
                <SelectContent>
                  {masterRecords.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name} ({r.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">The raw value will be auto-added as an alias.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMapDialog(null)}>Cancel</Button>
            <Button onClick={doMap} disabled={busy || !selectedMasterId} data-testid="confirm-map-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1" />} Map
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
